import { FEATURES } from '../../app/terminology/features';
import { SafeSaveBar as SaveBar } from '../../app/components/SafeAppBridge';
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { choiceValues, fieldChecked, fieldValue, useModalOpen, usePolarisEvents, useRowSelection } from '../../app/components/polaris';
import RowCheckbox from '../../app/components/RowCheckbox';
import Combobox from '../../app/components/Combobox';
import ProductThumbnail from '../../app/components/ProductThumbnail';
import { observer } from 'mobx-react-lite';
import agent from '../../app/api/agent';
import { getApiErrorMessage } from '../../app/api/error';
import { Product, ProductMappingStatus, RealtimeProduct, RealtimeProductListResult } from '../../app/models/Product';
import type { AtdProductListItem } from '../../app/models/Atd';
import { useStore } from '../../app/stores/store';
import { getShopifyAppHandle, getShopifyStoreHandle } from '../../app/utils/shopifyAdmin';
import { truncateText } from '../../app/utils/truncateText';
import { productStatusLabel } from '../../app/utils/productStatus';
import type { FitmentCatalogSettings } from '../../app/models/FitmentCatalog';
import type { CollectionMappingListResult, SelectedCollectionMapping } from '../../app/models/CollectionMapping';
import CollectionMappingsPanel from './CollectionMappingsPanel';
import MappingFilters, { type AppliedMappingFilter, type MappingFilter, type MappingSortOption } from './MappingFilters';
import ErrorPreviewControls from './ErrorPreviewControls';
import { PageQueryCache, isBrowseRequestCancelled } from './PageQueryCache';
import {
    getFitmentFieldEmptyOptionLabel,
    getFitmentFieldValue,
    getLegacyFieldValues,
    getVisibleFitmentFields,
    hasAnyFitmentFieldValue,
    hasRequiredFitmentFieldValues,
    isFitmentFieldDisabled,
    normalizeFitmentFieldValues,
    normalizeFitmentKey,
    updateFitmentFieldValue,
    type FitmentFieldValues,
} from '../../app/utils/fitmentFields';

type SelectOption = { label: string; value: string };
type ProductBrowseResult = RealtimeProductListResult & {
    mappedCount?: number;
    collectionMapping?: SelectedCollectionMapping;
};
type MappingCheck = { key: string; revision: number; result: ProductMappingStatus };
type FitmentField = ReturnType<typeof getVisibleFitmentFields>[number];

const parseYearValue = (value: string): number | null => {
    const trimmed = value.trim();
    return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
};

const isStandardFitmentField = (field: FitmentField, key: string) =>
    (field.key || '').trim().toLowerCase() === key;

const orderFitmentFieldsForPlp = (fields: FitmentField[], yearField?: FitmentField) => {
    if (!yearField) {
        return fields;
    }

    const makeField = fields.find((field) => isStandardFitmentField(field, 'make'));
    const modelField = fields.find((field) => isStandardFitmentField(field, 'model'));
    const prioritizedFields = [makeField, modelField].filter((field): field is FitmentField => !!field);
    const prioritizedSet = new Set(prioritizedFields);

    return [
        ...prioritizedFields,
        ...fields.filter((field) => field !== yearField && !prioritizedSet.has(field)),
        yearField,
    ];
};

const getYearRangeValues = (startValue: string, endValue: string) => {
    const start = startValue.trim();
    const end = endValue.trim();

    if (!start && !end) {
        return { values: [''], error: '' };
    }

    if (!end || end === start) {
        return { values: [start], error: '' };
    }

    if (!start) {
        return { values: [], error: 'Choose a starting year before choosing Year To.' };
    }

    const startYear = parseYearValue(start);
    const endYear = parseYearValue(end);
    if (startYear == null || endYear == null) {
        return { values: [], error: 'Year ranges must use whole-number years.' };
    }

    if (startYear > endYear) {
        return { values: [], error: 'Year To must be the same as or after the starting year.' };
    }

    return {
        values: Array.from({ length: endYear - startYear + 1 }, (_, index) => `${startYear + index}`),
        error: '',
    };
};

const getSelectedFitmentRangeLabel = (
    fields: FitmentField[],
    values: FitmentFieldValues,
    yearField?: FitmentField,
    yearTo = '',
) =>
    fields
        .map((field) => {
            const value = getFitmentFieldValue(values, field);
            if (!value) {
                return '';
            }

            if (yearField && field === yearField && yearTo.trim() && yearTo.trim() !== value.trim()) {
                return `${value}-${yearTo.trim()}`;
            }

            return value;
        })
        .filter(Boolean)
        .join(' ');

declare global {
    interface Window {
        shopify?: {
            idToken?: () => Promise<string>;
            toast?: { show?: (msg: string | { message: string; duration?: number }) => void };
        };
    }
}

const showToast = (msg: string) => window.shopify?.toast?.show?.(msg);

const PAGE_SIZE = 50;
const PRODUCT_TITLE_DISPLAY_LIMIT = 80;

const toRealtimeProduct = (product: Product): RealtimeProduct => ({
    id: product.id,
    title: product.title,
    sku: product.sku,
    vendor: '',
    fitmentCount: product.fitmentCount,
    featuredImage: product.featuredImage,
    status: product.status ?? '',
    createdAt: '',
    updatedAt: '',
    isMappedToSelectedVehicle: product.isMappedToSelectedVehicle,
    isAtdProduct: product.isAtdProduct,
});

const normalizeIds = (ids: string[]) => [...ids].sort((left, right) => left.localeCompare(right));

const idsEqual = (left: string[], right: string[]) =>
    normalizeIds(left).join('|') === normalizeIds(right).join('|');

const getAtdProductTitle = (product: AtdProductListItem) =>
    [product.brandName, product.productName, product.size]
        .filter((value) => value && value.trim())
        .join(' - ') || product.partNumber || `ATD product ${product.id}`;

const getAtdStatus = (product: AtdProductListItem) => {
    if (product.isDiscontinued) return { label: 'Discontinued', tone: 'critical' as const };
    if (!product.isActive) return { label: 'Inactive', tone: 'attention' as const };
    return { label: 'Active', tone: 'success' as const };
};

const isAtdProductManuallyIncluded = (product: AtdProductListItem) =>
    product.manualSyncAction === 'Link';

const parseAtdLinkStatusFromSearch = (searchParams: URLSearchParams) => {
    const value = searchParams.get('linkStatus')?.trim().toLowerCase();
    return value === 'linked' || value === 'unlinked' ? value : 'all';
};

const parseFieldValuesFromSearch = (searchParams: URLSearchParams): FitmentFieldValues => {
    const values: FitmentFieldValues = {};
    const rawFieldValues = searchParams.get('fieldValues');

    if (rawFieldValues) {
        try {
            const parsed = JSON.parse(rawFieldValues) as Record<string, string>;
            Object.entries(parsed).forEach(([key, value]) => {
                if (typeof value === 'string' && value.trim()) {
                    values[key.trim().toLowerCase()] = value.trim();
                }
            });
        } catch {
            // Ignore malformed URL state and fall back to legacy query params.
        }
    }

    ['year', 'make', 'model', 'submodel'].forEach((key) => {
        const value = searchParams.get(key);
        if (value?.trim()) values[key] = value.trim();
    });

    return values;
};

const writeFitmentSelectionToSearchParams = (
    searchParams: URLSearchParams,
    values: FitmentFieldValues,
    yearTo: string,
) => {
    const legacyKeys = new Set(['year', 'make', 'model', 'submodel']);
    const customValues: FitmentFieldValues = {};

    // Replace the complete selector payload so navigation cannot combine a
    // current vehicle with stale values left by a previous selection.
    ['year', 'make', 'model', 'submodel', 'fieldValues', 'yearTo']
        .forEach((key) => searchParams.delete(key));

    Object.entries(values).forEach(([rawKey, rawValue]) => {
        const key = rawKey.trim().toLowerCase();
        const value = rawValue.trim();
        if (!key || !value) return;

        if (legacyKeys.has(key)) {
            searchParams.set(key, value);
        } else {
            customValues[key] = value;
        }
    });

    if (Object.keys(customValues).length > 0) {
        searchParams.set('fieldValues', JSON.stringify(customValues));
    }

    if (yearTo.trim()) {
        searchParams.set('yearTo', yearTo.trim());
    }
};

const includeSelectedOption = (options: SelectOption[], selectedValue: string): SelectOption[] => {
    if (!selectedValue || options.some((option) => option.value === selectedValue)) {
        return options;
    }

    return [
        ...options,
        { label: selectedValue, value: selectedValue },
    ];
};

type MappingRowState = 'existing' | 'pending-add' | 'pending-remove' | 'unmapped';
type FitmentRemoval = {
    productIds: number[] | null;
    scope?: { query: string; hasFitment: string; status: string; vendor: string; tag: string };
};

const ProductsRealtimePage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { userStore } = useStore();
    const shopDomain = userStore.user?.shopDomain;
    // A new merchant owns a new cache bucket; view changes within that merchant
    // retain the same caches, and the cleanup below cancels the previous bucket.
    const { productCache, collectionCache, mappingCache } = useMemo(() => ({
        shopDomain,
        productCache: new PageQueryCache<ProductBrowseResult>(),
        collectionCache: new PageQueryCache<CollectionMappingListResult>(),
        mappingCache: new PageQueryCache<ProductMappingStatus>(),
    }), [shopDomain]);
    const [mappingRevision, setMappingRevision] = useState(0);
    const mappingRevisionRef = useRef(0);
    const [mappingCheck, setMappingCheck] = useState<MappingCheck>();
    const [listMappingCheck, setListMappingCheck] = useState<MappingCheck>();
    const [mappingCheckError, setMappingCheckError] = useState('');
    const invalidateBrowseCache = useCallback(() => {
        productCache.clear();
        collectionCache.clear();
        mappingCache.clear();
        mappingRevisionRef.current += 1;
        setMappingRevision(mappingRevisionRef.current);
        setListMappingCheck(undefined);
    }, [productCache, collectionCache, mappingCache]);
    useEffect(() => () => {
        productCache.clear();
        collectionCache.clear();
        mappingCache.clear();
    }, [productCache, collectionCache, mappingCache]);
    const [hasActiveAtdProfile, setHasActiveAtdProfile] = useState(false);
    const [atdProfileChecked, setAtdProfileChecked] = useState(false);
    const [sourceTab, setSourceTab] = useState(() => searchParams.get('tab') === 'atd' ? 1 : 0);
    const [mappingView, setMappingView] = useState<'products' | 'collections'>(() =>
        searchParams.get('view') === 'collections' ? 'collections' : 'products');
    const [collectionMappingDirty, setCollectionMappingDirty] = useState(false);
    const [fitmentCatalog, setFitmentCatalog] = useState<FitmentCatalogSettings | null>(null);
    const selectorFields = useMemo(() => getVisibleFitmentFields(fitmentCatalog), [fitmentCatalog]);
    const standardYearField = useMemo(
        () => selectorFields.find((field) => isStandardFitmentField(field, 'year')),
        [selectorFields],
    );
    const productListSelectorFields = useMemo(
        () => orderFitmentFieldsForPlp(selectorFields, standardYearField),
        [selectorFields, standardYearField],
    );
    const selectorFieldKey = useMemo(
        () => selectorFields.map((field) => normalizeFitmentKey(field)).join('|'),
        [selectorFields],
    );

    // Real-time Shopify data + cursor pagination bookkeeping.
    const [products, setProducts] = useState<RealtimeProduct[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasNext, setHasNext] = useState(false);
    const [nextCursor, setNextCursor] = useState('');
    const [cursorByPage, setCursorByPage] = useState<Record<number, string>>({ 1: '' });
    const [page, setPage] = useState(1);
    const [productLoadError, setProductLoadError] = useState<{
        message: string; targetPage: number; query: string; cursor: string; sort: string;
    }>();
    const [productActionError, setProductActionError] = useState('');

    // Legacy-like filters. Query goes to API; the rest are local filters to keep UI matching legacy.
    const [queryValue, setQueryValue] = useState<string>('');
    const [productQuery, setProductQuery] = useState('');
    useEffect(() => {
        if (!queryValue) { setProductQuery(''); return; }
        const timer = window.setTimeout(() => setProductQuery(queryValue), 250);
        return () => window.clearTimeout(timer);
    }, [queryValue]);
    const [hasFitment, setHasFitment] = useState<string[]>([]);
    const [productStatus, setProductStatus] = useState<string[]>([]);

    const [selectedFitmentValues, setSelectedFitmentValues] = useState<FitmentFieldValues>(() => parseFieldValuesFromSearch(searchParams));
    const [selectedFitmentYearTo, setSelectedFitmentYearTo] = useState(() => searchParams.get('yearTo')?.trim() ?? '');
    const normalizedSelectedFitmentValues = useMemo(
        () => normalizeFitmentFieldValues(selectorFields, selectedFitmentValues),
        [selectorFields, selectedFitmentValues],
    );
    const selectedLegacyValues = getLegacyFieldValues(normalizedSelectedFitmentValues);
    const selectedYear = selectedLegacyValues.year;
    const selectedYearRange = useMemo(
        () => standardYearField
            ? getYearRangeValues(selectedYear, selectedFitmentYearTo)
            : { values: [''], error: '' },
        [selectedFitmentYearTo, selectedYear, standardYearField],
    );
    const selectedYearToForRequest = standardYearField && !selectedYearRange.error
        ? selectedFitmentYearTo.trim()
        : '';
    const selectedMake = selectedLegacyValues.make;
    const selectedModel = selectedLegacyValues.model;
    const [mappedOnly, setMappedOnly] = useState(() => searchParams.get('mappedOnly') === 'true');
    const latestProductRequestIdRef = useRef(0);

    const [fieldOptions, setFieldOptions] = useState<Record<string, SelectOption[]>>({});
    const [loadingFieldOptions, setLoadingFieldOptions] = useState(false);


    const [sortSelected, setSortSelected] = useState(['created desc']);
    const searchParamString = searchParams.toString();
    const [mappingSubmitting, setMappingSubmitting] = useState(false);
    const [baselineMappedProductIds, setBaselineMappedProductIds] = useState<string[]>([]);
    const [draftMappedProductIds, setDraftMappedProductIds] = useState<string[]>([]);
    const [atdProducts, setAtdProducts] = useState<AtdProductListItem[]>([]);
    const [atdLoading, setAtdLoading] = useState(false);
    const [atdLoadError, setAtdLoadError] = useState<{ message: string; targetPage: number }>();
    const [atdActionError, setAtdActionError] = useState('');
    const latestAtdRequestId = useRef(0);
    const [atdPage, setAtdPage] = useState(1);
    const [atdHasNext, setAtdHasNext] = useState(false);
    const [atdTotalCount, setAtdTotalCount] = useState(0);
    const [atdQuery, setAtdQuery] = useState('');
    const [atdStatus, setAtdStatus] = useState('all');
    const [atdLinkStatus, setAtdLinkStatus] = useState(() => parseAtdLinkStatusFromSearch(searchParams));
    const [atdSort, setAtdSort] = useState('partnumber asc');

    const currentSortField = (sortSelected[0] || 'created desc').split(' ')[0];
    const hasVehicleContext = Boolean(
        productListSelectorFields.length > 0
        && !selectedYearRange.error
        && (productListSelectorFields.some((field) => field.isRequired)
            ? hasRequiredFitmentFieldValues(productListSelectorFields, normalizedSelectedFitmentValues)
            : hasAnyFitmentFieldValue(productListSelectorFields, normalizedSelectedFitmentValues))
    );

    const effectiveMappedOnly = mappedOnly && hasVehicleContext;
    const useLocalProductList = currentSortField === 'fitments' || hasFitment.length > 0 || effectiveMappedOnly;
    const selectedVehicleLabel = getSelectedFitmentRangeLabel(
        productListSelectorFields,
        normalizedSelectedFitmentValues,
        standardYearField,
        selectedYearToForRequest,
    );
    const selectedVehicleContextKey = useMemo(() => {
        if (!hasVehicleContext) {
            return '';
        }

        return JSON.stringify({
            fields: Object.entries(normalizedSelectedFitmentValues)
                .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey)),
            yearTo: selectedYearToForRequest,
        });
    }, [hasVehicleContext, normalizedSelectedFitmentValues, selectedYearToForRequest]);
    // Vehicle changes affect product membership only in the mapped-only view.
    const listVehicleContextKey = effectiveMappedOnly ? selectedVehicleContextKey : '';
    const listVehicleSelection = useMemo<{ fieldValues: FitmentFieldValues; yearTo: string }>(() => {
        if (!listVehicleContextKey) return { fieldValues: {}, yearTo: '' };
        const selection = JSON.parse(listVehicleContextKey);
        return { fieldValues: Object.fromEntries(selection.fields), yearTo: selection.yearTo };
    }, [listVehicleContextKey]);
    const isAtdSourceTab = hasActiveAtdProfile && sourceTab === 1;
    const isCollectionsView = mappingView === 'collections';
    useEffect(() => { setProductActionError(''); }, [selectedVehicleContextKey, shopDomain]);

    useEffect(() => {
        const params = new URLSearchParams(searchParamString);
        const nextMappedOnly = params.get('mappedOnly') === 'true';

        setSourceTab(params.get('tab') === 'atd' ? 1 : 0);
        setMappingView(params.get('view') === 'collections' ? 'collections' : 'products');
        setSelectedFitmentValues(parseFieldValuesFromSearch(params));
        setSelectedFitmentYearTo(params.get('yearTo')?.trim() ?? '');
        setMappedOnly(nextMappedOnly);
        setAtdLinkStatus(parseAtdLinkStatusFromSearch(params));
    }, [searchParamString]);

    useEffect(() => {
        let cancelled = false;

        const loadAtdProfileState = async () => {
            try {
                const profile = await agent.Atd.profile();
                if (!cancelled) setHasActiveAtdProfile(Boolean(profile?.isActive));
            } catch {
                if (!cancelled) setHasActiveAtdProfile(false);
            } finally {
                if (!cancelled) setAtdProfileChecked(true);
            }
        };

        loadAtdProfileState();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (atdProfileChecked && !hasActiveAtdProfile && sourceTab === 1) {
            setSourceTab(0);
        }
    }, [atdProfileChecked, hasActiveAtdProfile, sourceTab]);

    const loadProducts = useCallback(async (targetPage: number, query: string, cursor: string = '', sort: string = 'created desc') => {
        if (isAtdSourceTab) {
            return;
        }

        const requestId = latestProductRequestIdRef.current + 1;
        const requestMappingRevision = mappingRevisionRef.current;
        latestProductRequestIdRef.current = requestId;
        const cacheKey = JSON.stringify({
            source: useLocalProductList ? 'local' : 'realtime',
            targetPage, cursor, query, sort, pageSize: PAGE_SIZE,
            hasFitment: hasFitment[0] || '', mappedOnly: effectiveMappedOnly,
            status: productStatus[0] || '',
            selection: listVehicleSelection,
        });
        setLoading(productCache.get(cacheKey) === undefined);
        setProductLoadError(undefined);
        try {
            const result = await productCache.load(cacheKey, async signal => {
                if (useLocalProductList) {
                    const localResult = await agent.Products.list(
                        targetPage,
                        query,
                        hasFitment[0] || '',
                        '',
                        '',
                        sort,
                        '',
                        listVehicleSelection.yearTo,
                        '',
                        '',
                        effectiveMappedOnly,
                        listVehicleSelection.fieldValues,
                        productStatus[0] || '',
                        signal,
                        false,
                    );
                    return {
                        products: localResult.products.map(toRealtimeProduct),
                        hasNext: localResult.hasNext,
                        mappedCount: localResult.mappedCount,
                        collectionMapping: localResult.collectionMapping,
                    };
                }
                return agent.Products.listRealtime(query, cursor, PAGE_SIZE, sort, productStatus[0] || '', signal);
            });
            if (requestId !== latestProductRequestIdRef.current) return;
            setProducts(result.products);
            // The mapped-only list already checked the same full vehicle context.
            // Bind its result to the exact shop, selection, page IDs and revision
            // so a stale list cannot enable mapping edits for another selection.
            setListMappingCheck(listVehicleContextKey && result.collectionMapping ? {
                key: JSON.stringify({
                    shopDomain,
                    vehicle: listVehicleContextKey,
                    productIds: result.products.map(product => product.id).sort((a, b) => a - b),
                }),
                revision: requestMappingRevision,
                result: {
                    mappedProductIds: result.products.filter(product => product.isMappedToSelectedVehicle).map(product => product.id),
                    mappedCount: result.mappedCount ?? 0,
                    collectionMapping: result.collectionMapping,
                },
            } : undefined);
            setHasNext(result.hasNext);
            setNextCursor(result.nextCursor ?? '');
            setPage(targetPage);
        } catch (error) {
            if (requestId === latestProductRequestIdRef.current && !isBrowseRequestCancelled(error)) {
                const message = await getApiErrorMessage(error, 'Products are temporarily unavailable.');
                if (requestId === latestProductRequestIdRef.current) {
                    setProductLoadError({ message, targetPage, query, cursor, sort });
                }
            }
        } finally {
            if (requestId === latestProductRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [productCache, productStatus, hasFitment, isAtdSourceTab, effectiveMappedOnly, listVehicleSelection, listVehicleContextKey, shopDomain, useLocalProductList]);

    useEffect(() => {
        if (isAtdSourceTab || isCollectionsView || queryValue !== productQuery) {
            latestProductRequestIdRef.current += 1;
            productCache.cancel();
            setLoading(!isAtdSourceTab && !isCollectionsView && queryValue !== productQuery);
            return;
        }

        // Fresh query or sort always resets paging to page 1.
        setCursorByPage({ 1: '' });
        setPage(1);
        loadProducts(1, productQuery, '', sortSelected[0] || 'created desc');
        return () => {
            latestProductRequestIdRef.current += 1;
            productCache.cancel();
        };
    }, [productCache, isAtdSourceTab, isCollectionsView, queryValue, productQuery, sortSelected, loadProducts]);

    const loadAtdProducts = useCallback(async (
        targetPage: number,
        query: string,
        status: string,
        linkStatus: string,
        sort: string,
    ) => {
        if (!hasActiveAtdProfile) {
            setAtdProducts([]);
            setAtdTotalCount(0);
            setAtdHasNext(false);
            return;
        }

        const requestId = ++latestAtdRequestId.current;
        setAtdLoading(true);
        setAtdLoadError(undefined);
        try {
            const result = await agent.Atd.products(targetPage, query, status, linkStatus, sort);
            if (requestId !== latestAtdRequestId.current) return;
            setAtdProducts(result.items);
            setAtdHasNext(result.hasNext);
            setAtdTotalCount(result.totalCount);
            setAtdPage(result.page);
        } catch (error) {
            const message = await getApiErrorMessage(error, 'ATD products are temporarily unavailable.');
            if (requestId === latestAtdRequestId.current) setAtdLoadError({ message, targetPage });
        } finally {
            if (requestId === latestAtdRequestId.current) setAtdLoading(false);
        }
    }, [hasActiveAtdProfile]);

    useEffect(() => {
        if (!isAtdSourceTab) {
            return;
        }

        setAtdPage(1);
        loadAtdProducts(1, atdQuery, atdStatus, atdLinkStatus, atdSort);
        return () => { latestAtdRequestId.current += 1; };
    }, [atdLinkStatus, atdQuery, atdSort, atdStatus, isAtdSourceTab, loadAtdProducts]);

    useEffect(() => {
        let cancelled = false;

        const loadFitmentCatalog = async () => {
            try {
                const catalog = await agent.Settings.fitmentCatalog();
                if (!cancelled) setFitmentCatalog(catalog);
            } catch {
                if (!cancelled) setFitmentCatalog(null);
            }
        };

        loadFitmentCatalog();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!fitmentCatalog) return;
        setSelectedFitmentValues((current) => normalizeFitmentFieldValues(selectorFields, current));
    }, [fitmentCatalog, selectorFieldKey, selectorFields]);

    useEffect(() => {
        if (!standardYearField) {
            setSelectedFitmentYearTo('');
        }
    }, [standardYearField]);

    useEffect(() => {
        let cancelled = false;

        const loadOptions = async () => {
            if (productListSelectorFields.length === 0) {
                setFieldOptions({});
                return;
            }

            setLoadingFieldOptions(true);
            try {
                // /products is the bulk-assignment surface, so it must use the
                // same full selector context as Vehicle page and PDP.
                const result = await agent.Vehicles.filterOptions(
                    normalizedSelectedFitmentValues.year || '',
                    normalizedSelectedFitmentValues.make || '',
                    '',
                    normalizedSelectedFitmentValues,
                    productListSelectorFields.map((field) => normalizeFitmentKey(field)),
                );

                if (cancelled) return;

                const nextOptions = productListSelectorFields.reduce<Record<string, SelectOption[]>>((accumulator, field) => {
                    const key = normalizeFitmentKey(field);
                    const values = result.optionsByField?.[key] ?? [];
                    accumulator[key] = [
                        { label: getFitmentFieldEmptyOptionLabel(field, 'all'), value: '' },
                        ...values.map((value) => ({ label: value, value })),
                    ];
                    return accumulator;
                }, {});

                setFieldOptions(nextOptions);
            } catch {
                if (!cancelled) setFieldOptions({});
            } finally {
                if (!cancelled) setLoadingFieldOptions(false);
            }
        };

        loadOptions();
        return () => {
            cancelled = true;
        };
    }, [normalizedSelectedFitmentValues, productListSelectorFields]);

    // Keep non-query filters client-side for this first realtime UI pass.
    const filteredProducts = useMemo(() => {
        if (useLocalProductList) {
            return products;
        }

        return products.filter((product) => {
            if (hasFitment[0] === 'has' && product.fitmentCount < 1) return false;
            if (hasFitment[0] === 'none' && product.fitmentCount > 0) return false;
            return true;
        });
    }, [products, hasFitment, useLocalProductList]);

    const resourceProducts = filteredProducts.map((p) => ({ ...p, id: String(p.id) }));

    const productSelection = useRowSelection(resourceProducts.map((product) => product.id));
    const {
        selected: selectedResources,
        allMatching: allResourcesSelected,
        clear: clearSelection,
    } = productSelection;

    const atdResourceProducts = atdProducts.map((p) => ({ ...p, id: String(p.id) }));
    const atdSelection = useRowSelection(atdResourceProducts.map((product) => product.id));
    const {
        selected: selectedAtdResources,
        allMatching: allAtdResourcesSelected,
        clear: clearAtdSelection,
    } = atdSelection;

    const mappingRequestKey = hasVehicleContext ? JSON.stringify({
        shopDomain,
        vehicle: selectedVehicleContextKey,
        productIds: resourceProducts.map(product => Number(product.id)).sort((a, b) => a - b),
    }) : '';
    const mappingReady = !loading && mappingCheck?.key === mappingRequestKey && mappingCheck?.revision === mappingRevision;
    const displayedMappedCount = mappingReady ? mappingCheck.result.mappedCount : null;
    const displayedCollectionMapping = mappingReady ? mappingCheck.result.collectionMapping : null;
    const mappingFailed = mappingCheckError === mappingRequestKey && Boolean(mappingRequestKey);

    useEffect(() => {
        if (!mappingRequestKey || isCollectionsView || isAtdSourceTab || loading) return;
        if (listMappingCheck?.key === mappingRequestKey && listMappingCheck.revision === mappingRevision) {
            setMappingCheck(listMappingCheck);
            setMappingCheckError('');
            return;
        }
        let cancelled = false;
        setMappingCheck(undefined);
        setMappingCheckError('');
        const request = JSON.parse(mappingRequestKey);
        const vehicle = JSON.parse(request.vehicle);
        void mappingCache.load(mappingRequestKey, signal => agent.Products.mappingStatus({
            productIds: request.productIds,
            fieldValues: Object.fromEntries(vehicle.fields),
            yearTo: vehicle.yearTo,
        }, signal)).then(result => {
            if (!cancelled) setMappingCheck({ key: mappingRequestKey, revision: mappingRevision, result });
        }).catch(error => {
            if (!cancelled && !isBrowseRequestCancelled(error)) setMappingCheckError(mappingRequestKey);
        });
        return () => { cancelled = true; mappingCache.cancel(); };
    }, [mappingCache, mappingRequestKey, mappingRevision, listMappingCheck, isCollectionsView, isAtdSourceTab, loading]);

    const mappedProductIdsOnPageKey = mappingReady ? normalizeIds(mappingCheck.result.mappedProductIds.map(String)).join('|') : '';
    const baselineMappedProductIdSet = useMemo(() => new Set(baselineMappedProductIds), [baselineMappedProductIds]);
    const draftMappedProductIdSet = useMemo(() => new Set(draftMappedProductIds), [draftMappedProductIds]);
    const visibleProductIds = useMemo(() => resourceProducts.map((product) => product.id), [resourceProducts]);
    const hasMappingDraftChanges = hasVehicleContext && mappingReady && !idsEqual(baselineMappedProductIds, draftMappedProductIds);
    const allVisibleProductsMapped = mappingReady && visibleProductIds.length > 0
        && visibleProductIds.every((id) => draftMappedProductIdSet.has(id));
    const someVisibleProductsMapped = mappingReady && visibleProductIds.some((id) => draftMappedProductIdSet.has(id));
    const visibleMappingCheckedState: boolean | 'indeterminate' = allVisibleProductsMapped
        ? true
        : someVisibleProductsMapped
            ? 'indeterminate'
            : false;

    useEffect(() => {
        if (!hasVehicleContext) {
            setBaselineMappedProductIds([]);
            setDraftMappedProductIds([]);
            return;
        }

        const nextMappedProductIds = mappedProductIdsOnPageKey
            ? mappedProductIdsOnPageKey.split('|')
            : [];
        setBaselineMappedProductIds(nextMappedProductIds);
        setDraftMappedProductIds(nextMappedProductIds);
    }, [hasVehicleContext, mappingRequestKey, mappedProductIdsOnPageKey]);

    useEffect(() => {
        if (hasVehicleContext) {
            clearSelection();
        }
    }, [clearSelection, hasVehicleContext]);

    const getMappingRowState = useCallback((productId: string): MappingRowState => {
        if (!mappingReady) return 'unmapped';
        const wasMapped = baselineMappedProductIdSet.has(productId);
        const isMapped = draftMappedProductIdSet.has(productId);

        if (wasMapped && isMapped) return 'existing';
        if (!wasMapped && isMapped) return 'pending-add';
        if (wasMapped && !isMapped) return 'pending-remove';
        return 'unmapped';
    }, [mappingReady, baselineMappedProductIdSet, draftMappedProductIdSet]);

    const selectedCount = allResourcesSelected ? resourceProducts.length : selectedResources.length;
    const [fitmentRemoval, setFitmentRemoval] = useState<FitmentRemoval>();
    const [removingFitments, setRemovingFitments] = useState(false);
    const [fitmentRemovalError, setFitmentRemovalError] = useState('');
    const removingFitmentsRef = useRef(false);

    const openRemoveFitments = () => {
        if (selectedCount < 1 || loading || removingFitmentsRef.current) return;
        // Freeze the reviewed selection. Omitting vehicle fields removes all
        // assignments, including when only part of the vehicle selector is set.
        setFitmentRemoval({
            productIds: allResourcesSelected ? null : selectedResources.map(Number),
            scope: allResourcesSelected ? {
                query: queryValue,
                hasFitment: hasFitment[0] || '',
                status: productStatus[0] || '',
                vendor: '',
                tag: '',
            } : undefined,
        });
        setFitmentRemovalError('');
    };

    const closeRemoveFitments = () => {
        if (!removingFitmentsRef.current) setFitmentRemoval(undefined);
    };

    const confirmRemoveFitments = async () => {
        if (!fitmentRemoval || removingFitmentsRef.current) return;
        removingFitmentsRef.current = true;
        setRemovingFitments(true);
        setFitmentRemovalError('');
        try {
            await agent.Products.bulkFitments({ action: 'delete', ...fitmentRemoval });
        } catch (error) {
            invalidateBrowseCache();
            setFitmentRemovalError(await getApiErrorMessage(error, 'Unable to remove fitments. Try again.'));
            removingFitmentsRef.current = false;
            setRemovingFitments(false);
            return;
        }

        setFitmentRemoval(undefined);
        clearSelection();
        invalidateBrowseCache();
        showToast('Fitments removed');
        try {
            await loadProducts(page, queryValue, cursorByPage[page] ?? '', sortSelected[0] || 'created desc');
        } finally {
            removingFitmentsRef.current = false;
            setRemovingFitments(false);
        }
    };

    const bulkActions = [
        {
            content: 'Remove all fitments',
            destructive: true,
            onAction: openRemoveFitments,
            disabled: selectedCount < 1 || loading || removingFitments,
        },
    ];

    const toggleProductMapping = useCallback((productId: string, checked: boolean) => {
        setDraftMappedProductIds((currentIds) => {
            const nextIds = new Set(currentIds);
            if (checked) {
                nextIds.add(productId);
            } else {
                nextIds.delete(productId);
            }

            return Array.from(nextIds);
        });
    }, []);

    const toggleVisibleProductMappings = useCallback((checked: boolean) => {
        setDraftMappedProductIds((currentIds) => {
            const nextIds = new Set(currentIds);
            if (checked) {
                visibleProductIds.forEach((id) => nextIds.add(id));
            } else {
                visibleProductIds.forEach((id) => nextIds.delete(id));
            }

            return Array.from(nextIds);
        });
    }, [visibleProductIds]);

    const discardMappingChanges = useCallback(() => {
        setProductActionError('');
        setDraftMappedProductIds(baselineMappedProductIds);
    }, [baselineMappedProductIds]);

    const saveMappingChanges = useCallback(async () => {
        if (!hasVehicleContext || !hasMappingDraftChanges) {
            return;
        }

        const baselineIds = new Set(baselineMappedProductIds);
        const draftIds = new Set(draftMappedProductIds);
        const productIdsToMap = draftMappedProductIds.filter((id) => !baselineIds.has(id)).map((id) => Number(id));
        const productIdsToUnmap = baselineMappedProductIds.filter((id) => !draftIds.has(id)).map((id) => Number(id));

        setMappingSubmitting(true);
        try {
            if (productIdsToMap.length > 0) {
                await agent.Products.bulkFitments({
                    action: 'add',
                    productIds: productIdsToMap,
                    year: selectedYear,
                    yearTo: selectedYearToForRequest,
                    make: selectedMake,
                    model: selectedModel,
                    submodel: selectedLegacyValues.submodel,
                    fieldValues: normalizedSelectedFitmentValues,
                });
            }

            if (productIdsToUnmap.length > 0) {
                await agent.Products.bulkFitments({
                    action: 'delete',
                    productIds: productIdsToUnmap,
                    year: selectedYear,
                    yearTo: selectedYearToForRequest,
                    make: selectedMake,
                    model: selectedModel,
                    submodel: selectedLegacyValues.submodel,
                    fieldValues: normalizedSelectedFitmentValues,
                });
            }

            showToast(`Mappings saved for ${selectedVehicleLabel}`);
            setProductActionError('');
            invalidateBrowseCache();
            await loadProducts(page, queryValue, cursorByPage[page] ?? '', sortSelected[0] || 'created desc');
        } catch (error: any) {
            invalidateBrowseCache();
            setProductActionError(await getApiErrorMessage(error, 'Mapping changes could not be completed.'));
        } finally {
            setMappingSubmitting(false);
        }
    }, [
        baselineMappedProductIds,
        invalidateBrowseCache,
        cursorByPage,
        draftMappedProductIds,
        hasMappingDraftChanges,
        hasVehicleContext,
        loadProducts,
        page,
        queryValue,
        selectedMake,
        selectedModel,
        selectedLegacyValues.submodel,
        selectedVehicleLabel,
        selectedYear,
        selectedYearToForRequest,
        normalizedSelectedFitmentValues,
        sortSelected,
    ]);

    const handleHasFitmentRemove = useCallback(() => setHasFitment([]), []);

    const filters: MappingFilter[] = [
        {
            key: 'status',
            label: 'Status',
            filter: (
                <s-choice-list
                    label="Status"
                    labelAccessibilityVisibility="exclusive"
                    values={productStatus}
                    onChange={(event) => setProductStatus(choiceValues(event))}
                >
                    <s-choice value="active">Active</s-choice>
                    <s-choice value="draft">Draft</s-choice>
                    <s-choice value="archived">Archived</s-choice>
                </s-choice-list>
            ),
        },
        {
            key: 'hasFitment',
            label: 'Has fitments',
            filter: (
                <s-choice-list
                    label="Has fitments"
                    labelAccessibilityVisibility="exclusive"
                    values={hasFitment}
                    onChange={(event) => setHasFitment(choiceValues(event))}
                >
                    <s-choice value="has">Has fitments</s-choice>
                    <s-choice value="none">No fitments</s-choice>
                </s-choice-list>
            ),
        },
    ];

    const appliedFilters: AppliedMappingFilter[] = [];
    if (productStatus.length > 0) {
        appliedFilters.push({
            key: 'status',
            label: `Status: ${productStatusLabel(productStatus[0])}`,
            onRemove: () => setProductStatus([]),
        });
    }
    if (hasFitment.length > 0) {
        appliedFilters.push({
            key: 'hasFitment',
            label: hasFitment[0] === 'has' ? 'Has fitments' : 'No fitments',
            onRemove: handleHasFitmentRemove,
        });
    }


    const sortOptions: MappingSortOption[] = [
        { label: 'Product title', value: 'title asc', directionLabel: 'A-Z' },
        { label: 'Product title', value: 'title desc', directionLabel: 'Z-A' },
        { label: 'Fitments', value: 'fitments asc', directionLabel: 'Lowest to highest' },
        { label: 'Fitments', value: 'fitments desc', directionLabel: 'Highest to lowest' },
        { label: 'Created', value: 'created asc', directionLabel: 'Oldest first' },
        { label: 'Created', value: 'created desc', directionLabel: 'Newest first' },
    ];

    const sortedResourceProducts = useMemo(() => {
        const [key, direction] = (sortSelected[0] || 'created desc').split(' ');
        const sign = direction === 'desc' ? -1 : 1;

        return [...resourceProducts].sort((a, b) => {
            if (key === 'fitments') return (a.fitmentCount - b.fitmentCount) * sign;
            if (key === 'created') return ((a.createdAt || '').localeCompare(b.createdAt || '')) * sign;
            if (key === 'updated') return ((a.updatedAt || '').localeCompare(b.updatedAt || '')) * sign;
            return a.title.localeCompare(b.title) * sign;
        });
    }, [resourceProducts, sortSelected]);

    const shopHandle = getShopifyStoreHandle(userStore.user?.shopDomain);
    const appHandle = getShopifyAppHandle();

    const buildProductAdminUrl = useCallback((productId: string) => {
        if (!shopHandle || !appHandle) {
            return `/products/${productId}`;
        }

        return `https://admin.shopify.com/store/${shopHandle}/apps/${appHandle}/products/${productId}`;
    }, [appHandle, shopHandle]);

    const handleTitleLinkClick = useCallback((event: Event, path: string) => {
        const mouse = event as MouseEvent;
        event.stopPropagation();

        // Let the browser handle modified clicks and context-menu driven actions.
        if (
            mouse.button !== 0 ||
            mouse.metaKey ||
            mouse.ctrlKey ||
            mouse.shiftKey ||
            mouse.altKey
        ) {
            return;
        }

        event.preventDefault();
        navigate(path);
    }, [navigate]);

    const selectedAtdCount = allAtdResourcesSelected ? atdResourceProducts.length : selectedAtdResources.length;
    const selectedAtdProductIds = useMemo(
        () => allAtdResourcesSelected
            ? atdProducts.map((product) => product.id)
            : selectedAtdResources.map((id) => Number(id)).filter((id) => Number.isFinite(id)),
        [allAtdResourcesSelected, atdProducts, selectedAtdResources],
    );

    const runManualAtdLinkAction = useCallback(async (action: 'Link' | 'Unlink') => {
        if (selectedAtdProductIds.length === 0) {
            return;
        }

        setAtdLoading(true);
        try {
            const result = await agent.Atd.saveProductManualLinks({
                action,
                atdProductIds: selectedAtdProductIds,
            });
            showToast(result.message);
            setAtdActionError('');
            clearAtdSelection();
            await loadAtdProducts(atdPage, atdQuery, atdStatus, atdLinkStatus, atdSort);
        } catch (error: any) {
            setAtdActionError(`Couldn’t ${action.toLowerCase()} the selected ATD products. ${await getApiErrorMessage(error, 'Please try again.')}`);
        } finally {
            setAtdLoading(false);
        }
    }, [
        atdLinkStatus,
        atdPage,
        atdQuery,
        atdSort,
        atdStatus,
        clearAtdSelection,
        loadAtdProducts,
        selectedAtdProductIds,
    ]);

    const atdPromotedBulkActions = [
        {
            content: 'Link selected',
            onAction: () => runManualAtdLinkAction('Link'),
            disabled: selectedAtdCount < 1 || atdLoading,
        },
        {
            content: 'Unlink selected',
            destructive: true,
            onAction: () => runManualAtdLinkAction('Unlink'),
            disabled: selectedAtdCount < 1 || atdLoading,
        },
    ];

    const handleSourceTabSelect = useCallback((selected: number) => {
        setSourceTab(selected);

        const nextParams = new URLSearchParams(searchParams);
        writeFitmentSelectionToSearchParams(
            nextParams,
            normalizedSelectedFitmentValues,
            selectedFitmentYearTo,
        );
        if (selected === 1) {
            nextParams.set('tab', 'atd');
        } else {
            nextParams.delete('tab');
        }

        setSearchParams(nextParams, { replace: true });
    }, [normalizedSelectedFitmentValues, searchParams, selectedFitmentYearTo, setSearchParams]);

    const handleMappingViewSelect = useCallback((view: 'products' | 'collections') => {
        if (view === mappingView) return;

        if ((mappingView === 'products' && hasMappingDraftChanges)
            || (mappingView === 'collections' && collectionMappingDirty)) {
            showToast('Save or discard the current mapping changes before switching views');
            return;
        }

        setMappingView(view);
        const nextParams = new URLSearchParams(searchParams);
        writeFitmentSelectionToSearchParams(
            nextParams,
            normalizedSelectedFitmentValues,
            selectedFitmentYearTo,
        );
        if (view === 'collections') {
            nextParams.set('view', 'collections');
        } else {
            nextParams.delete('view');
        }
        setSearchParams(nextParams, { replace: true });
    }, [
        collectionMappingDirty,
        hasMappingDraftChanges,
        mappingView,
        normalizedSelectedFitmentValues,
        searchParams,
        selectedFitmentYearTo,
        setSearchParams,
    ]);

    // View switches are page actions, so the admin title bar shows them.
    const sourceSwitch = hasActiveAtdProfile ? (
        <s-button slot="secondary-actions" onClick={() => handleSourceTabSelect(sourceTab === 1 ? 0 : 1)}>
            {sourceTab === 1 ? 'Shopify products' : 'ATD products'}
        </s-button>
    ) : undefined;

    const mappingViewSwitch = (
        <s-button slot="secondary-actions" icon={mappingView === 'products' ? 'collection' : 'product'}
            onClick={() => handleMappingViewSelect(mappingView === 'products' ? 'collections' : 'products')}>
            {mappingView === 'products' ? 'Collections' : 'Products'}
        </s-button>
    );

    const atdStatusOptions = [
        { label: 'All statuses', value: 'all' },
        { label: 'Active', value: 'active' },
        { label: 'Discontinued', value: 'discontinued' },
        { label: 'Inactive', value: 'inactive' },
        { label: 'Unavailable', value: 'unavailable' },
    ];

    const atdLinkOptions = [
        { label: 'All link states', value: 'all' },
        { label: 'Linked', value: 'linked' },
        { label: 'Unlinked', value: 'unlinked' },
    ];

    const atdSortOptions = [
        { label: 'Part number A-Z', value: 'partnumber asc' },
        { label: 'Part number Z-A', value: 'partnumber desc' },
        { label: 'Product A-Z', value: 'title asc' },
        { label: 'Product Z-A', value: 'title desc' },
        { label: 'Brand A-Z', value: 'brand asc' },
        { label: 'Brand Z-A', value: 'brand desc' },
        { label: 'Last updated newest', value: 'updated desc' },
        { label: 'Last updated oldest', value: 'updated asc' },
        { label: 'Linked first', value: 'linked desc' },
        { label: 'Unlinked first', value: 'linked asc' },
    ];

    const loadNextProductPage = async () => {
        if (useLocalProductList) {
            await loadProducts(page + 1, queryValue, '', sortSelected[0] || 'created desc');
            return;
        }

        if (!nextCursor) return;
        setCursorByPage((prev) => ({ ...prev, [page + 1]: nextCursor }));
        await loadProducts(page + 1, queryValue, nextCursor, sortSelected[0] || 'created desc');
    };

    const loadPreviousProductPage = async () => {
        if (useLocalProductList) {
            await loadProducts(page - 1, queryValue, '', sortSelected[0] || 'created desc');
            return;
        }

        await loadProducts(page - 1, queryValue, cursorByPage[page - 1] ?? '', sortSelected[0] || 'created desc');
    };

    const canPageProducts = queryValue === productQuery;
    const productTableEvents = usePolarisEvents({
        nextpage: () => { if (hasNext && canPageProducts) void loadNextProductPage(); },
        previouspage: () => { if (page > 1 && canPageProducts) void loadPreviousProductPage(); },
    });
    const atdTableEvents = usePolarisEvents({
        nextpage: () => { if (atdHasNext) void loadAtdProducts(atdPage + 1, atdQuery, atdStatus, atdLinkStatus, atdSort); },
        previouspage: () => { if (atdPage > 1) void loadAtdProducts(atdPage - 1, atdQuery, atdStatus, atdLinkStatus, atdSort); },
    });
    const productBannerEvents = usePolarisEvents({
        dismiss: () => { setProductLoadError(undefined); setProductActionError(''); },
    });
    const atdBannerEvents = usePolarisEvents({
        dismiss: () => { setAtdLoadError(undefined); setAtdActionError(''); },
    });
    const removeFitmentsModalRef = useModalOpen(Boolean(fitmentRemoval), closeRemoveFitments);

    // One row of vehicle fields on wide screens (Year and Year To side by side),
    // two columns on narrow ones. Polaris container values must not contain
    // commas inside a value, so columns are written out instead of repeat().
    const vehicleFieldCount = productListSelectorFields.length + (standardYearField ? 1 : 0);
    const vehicleGridColumns = `@container (inline-size > ${Math.max(560, vehicleFieldCount * 150)}px) ${'1fr '.repeat(vehicleFieldCount)}auto, 1fr 1fr`;
    const hasVehicleSelection = hasAnyFitmentFieldValue(productListSelectorFields, normalizedSelectedFitmentValues) || Boolean(selectedFitmentYearTo);

    const vehicleFields = (
        <s-query-container>
            <s-grid gridTemplateColumns={vehicleGridColumns} gap="small-200" alignItems="end">
                {productListSelectorFields.map((field) => {
                    const key = normalizeFitmentKey(field);
                    const selectedValue = getFitmentFieldValue(normalizedSelectedFitmentValues, field);
                    const options = includeSelectedOption(
                        fieldOptions[key] ?? [{ label: getFitmentFieldEmptyOptionLabel(field, 'all'), value: '' }],
                        selectedValue,
                    );
                    const fieldIndex = productListSelectorFields.findIndex((candidate) => candidate === field);
                    const yearFieldIndex = standardYearField
                        ? productListSelectorFields.findIndex((candidate) => candidate === standardYearField)
                        : -1;

                    return (
                        <React.Fragment key={key}>
                            <Combobox
                                label={field.label}
                                options={options}
                                value={selectedValue}
                                disabled={isFitmentFieldDisabled(productListSelectorFields, normalizedSelectedFitmentValues, field, false)}
                                loading={loadingFieldOptions}
                                placeholder={getFitmentFieldEmptyOptionLabel(field, 'select')}
                                onChange={(value) => {
                                    // Both mapping views share the configured
                                    // selector fields and normalized URL state.
                                    setSelectedFitmentValues((current) => updateFitmentFieldValue(productListSelectorFields, current, field, value));
                                    if (standardYearField && (field === standardYearField ? !value : fieldIndex < yearFieldIndex)) {
                                        setSelectedFitmentYearTo('');
                                    }
                                    setMappedOnly(false);
                                }}
                            />

                            {standardYearField && field === standardYearField && (
                                <Combobox
                                    label="Year To"
                                    options={includeSelectedOption(fieldOptions[key] ?? [], selectedFitmentYearTo)}
                                    value={selectedFitmentYearTo}
                                    disabled={!selectedValue}
                                    loading={loadingFieldOptions}
                                    error={selectedYearRange.error}
                                    placeholder="Select ending year"
                                    onChange={(value) => {
                                        setSelectedFitmentYearTo(value);
                                        setMappedOnly(false);
                                    }}
                                />
                            )}
                        </React.Fragment>
                    );
                })}

                <s-button
                    variant="tertiary"
                    onClick={() => {
                        setSelectedFitmentValues({});
                        setSelectedFitmentYearTo('');
                        setMappedOnly(false);
                    }}
                    disabled={!hasVehicleSelection}
                >
                    Clear
                </s-button>
            </s-grid>
        </s-query-container>
    );

    const fitCountLabel = displayedMappedCount == null
        ? mappingFailed ? 'Fit check failed' : 'Checking fit…'
        : `${displayedMappedCount.toLocaleString()} ${displayedMappedCount === 1 ? 'product fits' : 'products fit'}`;

    const vehicleSummary = hasVehicleContext && !isCollectionsView && (
        <s-stack gap="small-200">
            <s-stack direction="inline" gap="small-200" alignItems="center">
                <s-text fontWeight="semibold">{selectedVehicleLabel}</s-text>
                <s-text color="subdued">{fitCountLabel}</s-text>
                {mappingFailed && (
                    <s-button variant="tertiary" accessibilityLabel="Retry fit check" onClick={() => setMappingRevision(value => value + 1)}>Retry</s-button>
                )}
            </s-stack>
            {displayedCollectionMapping && displayedCollectionMapping.state !== 'unmapped' && (
                <s-banner tone={displayedCollectionMapping.state === 'mixed' ? 'warning' : 'info'}
                    heading={displayedCollectionMapping.state === 'mapped'
                        ? 'This vehicle is mapped to a collection'
                        : 'This year range has mixed collection mappings'}>
                    {displayedCollectionMapping.state === 'mapped' ? (
                        <>
                            Mapped collection: <s-text fontWeight="semibold">“{displayedCollectionMapping.collectionTitle}”</s-text>.{' '}
                            Product mappings for this vehicle remain saved. How this mapping is used on the storefront is controlled in Settings.
                        </>
                    ) : (
                        `${displayedCollectionMapping.mappedVehicleCount} of ${displayedCollectionMapping.selectedVehicleCount} selected vehicles are mapped to collections.`
                    )}
                    <s-button slot="secondary-actions" onClick={() => handleMappingViewSelect('collections')}>
                        Manage mapping
                    </s-button>
                </s-banner>
            )}
        </s-stack>
    );

    const vehicleBand = productListSelectorFields.length > 0 && (
        <s-stack gap="small-200">
            {vehicleFields}
            {vehicleSummary}
        </s-stack>
    );

    // The s-page heading shows only in the admin title bar; repeat it on the page.
    const pageHeader = (
        <s-heading fontSize="large-200">{isCollectionsView ? 'Collections' : FEATURES.products}</s-heading>
    );

    const titleTooltip = (id: string, title: string) => (
        <s-tooltip id={id}>{title}</s-tooltip>
    );

    // Replaces the search and filter row while products are selected, as in admin.
    const productSelectionBar = !hasVehicleContext && (productSelection.selected.length > 0 || allResourcesSelected) && (
        <s-stack direction="inline" gap="small-200" alignItems="center">
            <s-checkbox
                accessibilityLabel="Clear selection"
                checked
                indeterminate={!productSelection.allOnPage && !allResourcesSelected}
                onChange={() => productSelection.clear()}
            />
            <s-text fontWeight="semibold">
                {allResourcesSelected
                    ? 'All matching products selected'
                    : `${productSelection.selected.length.toLocaleString()} selected`}
            </s-text>
            {productSelection.allOnPage && hasNext && !allResourcesSelected && (
                <s-button variant="tertiary" onClick={() => productSelection.setAllMatching(true)}>
                    Select all matching products
                </s-button>
            )}
            {bulkActions.map((action) => (
                <s-button key={action.content} tone="critical" icon="delete" onClick={action.onAction} disabled={action.disabled}>
                    {action.content}
                </s-button>
            ))}
        </s-stack>
    );

    const hasProductFilters = Boolean(queryValue) || productStatus.length > 0 || hasFitment.length > 0 || (hasVehicleContext && mappedOnly);
    const clearProductFilters = () => {
        setProductStatus([]);
        setHasFitment([]);
        setQueryValue('');
        setMappedOnly(false);
    };

    const atdSelectionBar = (atdSelection.selected.length > 0 || allAtdResourcesSelected) && (
        <s-stack direction="inline" gap="small-200" alignItems="center">
            <s-text fontWeight="semibold">
                {allAtdResourcesSelected
                    ? 'All matching ATD products selected'
                    : `${atdSelection.selected.length.toLocaleString()} selected`}
            </s-text>
            {atdSelection.allOnPage && atdHasNext && !allAtdResourcesSelected && (
                <s-button variant="tertiary" onClick={() => atdSelection.setAllMatching(true)}>
                    Select all matching ATD products
                </s-button>
            )}
            {atdPromotedBulkActions.map((action) => (
                <s-button key={action.content} tone={action.destructive ? 'critical' : 'auto'}
                    onClick={action.onAction} disabled={action.disabled}>
                    {action.content}
                </s-button>
            ))}
        </s-stack>
    );

    return (
        <>
            <s-modal
                ref={removeFitmentsModalRef}
                id="pf-remove-fitments-modal"
                heading={fitmentRemoval?.productIds
                    ? `Remove all fitments from ${fitmentRemoval.productIds.length} ${fitmentRemoval.productIds.length === 1 ? 'product' : 'products'}?`
                    : 'Remove all fitments from all matching products?'}
            >
                <s-stack gap="base">
                    {fitmentRemovalError && <s-banner tone="critical">{fitmentRemovalError}</s-banner>}
                    <s-paragraph>
                        {fitmentRemoval?.productIds
                            ? 'This will remove every vehicle assignment from the selected products.'
                            : 'This will remove every vehicle assignment from all products matching the current filters, including products on other pages.'}
                    </s-paragraph>
                    <s-paragraph>The products themselves will remain in your store.</s-paragraph>
                </s-stack>
                <s-button slot="primary-action" variant="primary" tone="critical" loading={removingFitments} onClick={confirmRemoveFitments}>
                    Remove fitments
                </s-button>
                <s-button slot="secondary-actions" disabled={removingFitments} onClick={closeRemoveFitments}>
                    Cancel
                </s-button>
            </s-modal>
            <SaveBar id="products-mapping-save-bar" open={!isCollectionsView && !isAtdSourceTab && hasMappingDraftChanges}>
                <button onClick={discardMappingChanges} disabled={loading || mappingSubmitting}>
                    Discard
                </button>
                <button variant="primary" onClick={saveMappingChanges} disabled={loading || mappingSubmitting} loading={mappingSubmitting}>
                    Save
                </button>
            </SaveBar>
            <s-page heading={isCollectionsView ? 'Collections' : FEATURES.products} inlineSize="large">
                {mappingViewSwitch}
                {!isCollectionsView && sourceSwitch}
                <s-stack gap="base">
                {pageHeader}

                {isCollectionsView ? (
                    <>
                        <s-section accessibilityLabel="Collection mappings">
                            {vehicleBand && <s-box paddingBlockEnd="base">{vehicleBand}</s-box>}
                            <CollectionMappingsPanel
                                shopDomain={shopDomain}
                                cache={collectionCache}
                                onMappingSaved={invalidateBrowseCache}
                                hasVehicleContext={hasVehicleContext}
                                selectedVehicleLabel={selectedVehicleLabel}
                                year={selectedYear}
                                yearTo={selectedYearToForRequest}
                                make={selectedMake}
                                model={selectedModel}
                                submodel={selectedLegacyValues.submodel}
                                fieldValues={normalizedSelectedFitmentValues}
                                onDirtyChange={setCollectionMappingDirty}
                            />
                        </s-section>
                    </>
                ) : isAtdSourceTab ? (
                    <s-section accessibilityLabel="ATD products" padding="none">
                        <s-stack gap="base" padding="base">
                            <ErrorPreviewControls search={searchParamString} shopDomain={shopDomain}
                                cases={[
                                    { label: 'Preview ATD loading error', onShow: () => { setAtdActionError(''); setAtdLoadError({ message: 'Sample error: ATD products are temporarily unavailable.', targetPage: atdPage }); } },
                                    { label: 'Preview ATD link error', onShow: () => { setAtdLoadError(undefined); setAtdActionError('Sample error: Couldn’t link the selected ATD products.'); } },
                                    { label: 'Preview ATD unlink error', onShow: () => { setAtdLoadError(undefined); setAtdActionError('Sample error: Couldn’t unlink the selected ATD products.'); } },
                                ]}
                                onClear={() => { setAtdLoadError(undefined); setAtdActionError(''); }} />
                            {(atdLoadError || atdActionError) && (
                                <s-banner ref={atdBannerEvents} tone="critical" dismissible
                                    heading={atdActionError ? 'ATD product changes need attention' : 'Couldn’t load ATD products'}>
                                    {atdActionError && <s-paragraph>{atdActionError} Review the selected products and try the action again.</s-paragraph>}
                                    {atdLoadError && <s-paragraph>{atdLoadError.message}</s-paragraph>}
                                    {atdLoadError && (
                                        <s-button slot="secondary-actions" loading={atdLoading}
                                            onClick={() => { void loadAtdProducts(atdLoadError.targetPage, atdQuery, atdStatus, atdLinkStatus, atdSort); }}>
                                            Retry loading ATD products
                                        </s-button>
                                    )}
                                </s-banner>
                            )}
                            <s-query-container>
                                <s-grid gridTemplateColumns="@container (inline-size > 720px) 2fr 1fr 1fr 1fr auto, 1fr" gap="small-200" alignItems="end">
                                    <s-search-field
                                        label="Search ATD products"
                                        value={atdQuery}
                                        onInput={(event) => setAtdQuery(fieldValue(event))}
                                        placeholder="Part #, brand, product, UPC"
                                    />
                                    <s-select label="Status" value={atdStatus} onChange={(event) => setAtdStatus(fieldValue(event))}>
                                        {atdStatusOptions.map((option) => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
                                    </s-select>
                                    <s-select label="Link" value={atdLinkStatus} onChange={(event) => setAtdLinkStatus(fieldValue(event))}>
                                        {atdLinkOptions.map((option) => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
                                    </s-select>
                                    <s-select label="Sort" value={atdSort} onChange={(event) => setAtdSort(fieldValue(event))}>
                                        {atdSortOptions.map((option) => <s-option key={option.value} value={option.value}>{option.label}</s-option>)}
                                    </s-select>
                                    <s-button
                                        icon="refresh"
                                        onClick={() => loadAtdProducts(atdPage, atdQuery, atdStatus, atdLinkStatus, atdSort)}
                                        loading={atdLoading}
                                    >
                                        Refresh
                                    </s-button>
                                </s-grid>
                            </s-query-container>
                            <s-text color="subdued">{atdTotalCount.toLocaleString()} ATD products</s-text>
                            {atdSelectionBar}
                        </s-stack>

                        <s-table
                            ref={atdTableEvents}
                            loading={atdLoading}
                            paginate
                            hasNextPage={atdHasNext}
                            hasPreviousPage={atdPage > 1}
                        >
                            <s-table-header-row>
                                <s-table-header listSlot="primary">
                                    <s-stack direction="inline" gap="base" alignItems="center">
                                        <s-checkbox
                                            accessibilityLabel="Select all ATD products on this page"
                                            checked={atdSelection.allOnPage}
                                            indeterminate={atdSelection.someOnPage}
                                            disabled={atdProducts.length === 0}
                                            onChange={(event) => atdSelection.togglePage(fieldChecked(event))}
                                        />
                                        <s-text>Product</s-text>
                                    </s-stack>
                                </s-table-header>
                                <s-table-header>Part #</s-table-header>
                                <s-table-header>Brand</s-table-header>
                                <s-table-header>Size</s-table-header>
                                <s-table-header>Status</s-table-header>
                                <s-table-header>Shopify</s-table-header>
                                <s-table-header>Qty</s-table-header>
                            </s-table-header-row>
                            <s-table-body>
                                {atdProducts.map((product) => {
                                    const productId = String(product.id);
                                    const status = getAtdStatus(product);
                                    const title = getAtdProductTitle(product);
                                    const displayTitle = truncateText(title, PRODUCT_TITLE_DISPLAY_LIMIT);
                                    const showTitleTooltip = title.length > PRODUCT_TITLE_DISPLAY_LIMIT;
                                    const linkedProductTitle = String(product.shopifyProductTitle || product.shopifyProductId);
                                    const linkedProductDisplayTitle = truncateText(linkedProductTitle, PRODUCT_TITLE_DISPLAY_LIMIT);
                                    const showLinkedProductTooltip = linkedProductTitle.length > PRODUCT_TITLE_DISPLAY_LIMIT;
                                    const linkId = `pf-atd-product-${productId}`;

                                    return (
                                        <s-table-row key={product.id} clickDelegate={linkId}>
                                            <s-table-cell>
                                                <s-stack direction="inline" gap="small" alignItems="center">
                                                    <RowCheckbox
                                                        accessibilityLabel={`Select ${title}`}
                                                        checked={allAtdResourcesSelected || atdSelection.isSelected(productId)}
                                                        onChange={(checked) => atdSelection.toggle(productId, checked)}
                                                    />
                                                    <ProductThumbnail source={product.primaryImageUrl} alt={title} size="small" />
                                                    <s-stack gap="none">
                                                        <s-link
                                                            id={linkId}
                                                            href={`/products/atd/${product.id}`}
                                                            accessibilityLabel={title}
                                                            interestFor={showTitleTooltip ? `${linkId}-tip` : undefined}
                                                            onClick={(event) => handleTitleLinkClick(event, `/products/atd/${product.id}`)}
                                                        >
                                                            {displayTitle}
                                                        </s-link>
                                                        {showTitleTooltip && titleTooltip(`${linkId}-tip`, title)}
                                                        <s-text color="subdued">{product.categoryName || '-'}</s-text>
                                                    </s-stack>
                                                </s-stack>
                                            </s-table-cell>
                                            <s-table-cell>{product.partNumber || product.manufacturerPartNumber || '-'}</s-table-cell>
                                            <s-table-cell>{product.brandName || '-'}</s-table-cell>
                                            <s-table-cell>{product.size || '-'}</s-table-cell>
                                            <s-table-cell><s-badge tone={status.tone === 'attention' ? 'caution' : status.tone}>{status.label}</s-badge></s-table-cell>
                                            <s-table-cell>
                                                {product.shopifyProductId ? (
                                                    <s-stack gap="none">
                                                        <s-text interestFor={showLinkedProductTooltip ? `${linkId}-linked-tip` : undefined}>
                                                            {linkedProductDisplayTitle}
                                                        </s-text>
                                                        {showLinkedProductTooltip && titleTooltip(`${linkId}-linked-tip`, linkedProductTitle)}
                                                        <s-text color="subdued">{product.shopifyProductStatus || 'Linked'}</s-text>
                                                    </s-stack>
                                                ) : isAtdProductManuallyIncluded(product) ? (
                                                    <s-badge tone="info">Manual link pending</s-badge>
                                                ) : product.manualSyncAction === 'Unlink' ? (
                                                    <s-badge tone="caution">Manually unlinked</s-badge>
                                                ) : (
                                                    <s-badge tone="caution">Unlinked</s-badge>
                                                )}
                                            </s-table-cell>
                                            <s-table-cell>{product.availableQuantity ?? 0}</s-table-cell>
                                        </s-table-row>
                                    );
                                })}
                            </s-table-body>
                        </s-table>
                    </s-section>
                ) : (
            <>
                <ErrorPreviewControls search={searchParamString} shopDomain={shopDomain}
                    cases={[
                        { label: 'Preview product loading error', onShow: () => { setProductActionError(''); setProductLoadError({ message: 'Sample error: Shopify could not respond.', targetPage: page, query: queryValue, cursor: cursorByPage[page] ?? '', sort: sortSelected[0] || 'created desc' }); } },
                        { label: 'Preview mapping save error', onShow: () => { setProductLoadError(undefined); setProductActionError('Sample error: Mapping changes could not be completed.'); } },
                    ]}
                    onClear={() => { setProductLoadError(undefined); setProductActionError(''); }} />
                {(productLoadError || productActionError) && (
                    <s-banner ref={productBannerEvents} tone="critical" dismissible
                        heading={productActionError ? 'Product mapping changes need attention' : 'Couldn’t load products'}>
                        {productActionError && <>
                            <s-paragraph>{productActionError}</s-paragraph>
                            <s-paragraph>Some changes may have saved. Review the mappings before retrying.</s-paragraph>
                        </>}
                        {productLoadError && <s-paragraph>{productLoadError.message} Try loading the products again.</s-paragraph>}
                        {productLoadError && (
                            <s-button slot="secondary-actions" loading={loading}
                                onClick={() => { void loadProducts(productLoadError.targetPage, productLoadError.query, productLoadError.cursor, productLoadError.sort); }}>
                                Retry loading products
                            </s-button>
                        )}
                    </s-banner>
                )}

                <s-section accessibilityLabel={FEATURES.products}>
                    <s-stack gap="base">
                        {vehicleBand}
                        {productSelectionBar || (
                            <MappingFilters
                                hasVehicleContext={hasVehicleContext}
                                mappedOnly={mappedOnly}
                                onMappedOnlyChange={setMappedOnly}
                                mappedOnlyLabel="Fits this vehicle"
                                sortOptions={sortOptions}
                                sortSelected={sortSelected}
                                onSort={setSortSelected}
                                queryValue={queryValue}
                                queryPlaceholder="Search products"
                                onQueryChange={setQueryValue}
                                onClearAll={clearProductFilters}
                                filters={filters}
                                appliedFilters={appliedFilters}
                            />
                        )}
                        <s-table
                            ref={productTableEvents}
                            loading={loading}
                            paginate={sortedResourceProducts.length > 0 || page > 1}
                            hasNextPage={hasNext && canPageProducts}
                            hasPreviousPage={page > 1 && canPageProducts}
                        >
                            <s-table-header-row>
                                <s-table-header listSlot="primary">
                                    <s-stack direction="inline" gap="base" alignItems="center">
                                        {hasVehicleContext ? (
                                            <s-checkbox
                                                accessibilityLabel={`Mark all products on this page as fitting ${selectedVehicleLabel}`}
                                                checked={visibleMappingCheckedState === true}
                                                indeterminate={visibleMappingCheckedState === 'indeterminate'}
                                                onChange={(event) => toggleVisibleProductMappings(fieldChecked(event))}
                                                disabled={!mappingReady || mappingSubmitting || visibleProductIds.length === 0}
                                            />
                                        ) : (
                                            <s-checkbox
                                                accessibilityLabel="Select all products on this page"
                                                checked={productSelection.allOnPage}
                                                indeterminate={productSelection.someOnPage}
                                                disabled={sortedResourceProducts.length === 0}
                                                onChange={(event) => productSelection.togglePage(fieldChecked(event))}
                                            />
                                        )}
                                        <s-text>Product</s-text>
                                    </s-stack>
                                </s-table-header>
                                <s-table-header listSlot="inline">Status</s-table-header>
                                <s-table-header listSlot="labeled">SKU</s-table-header>
                                {hasActiveAtdProfile && <s-table-header listSlot="labeled">Source</s-table-header>}
                                <s-table-header listSlot="labeled">Fitments</s-table-header>
                                {hasVehicleContext && <s-table-header listSlot="inline">Fits vehicle</s-table-header>}
                            </s-table-header-row>
                            <s-table-body>
                                {sortedResourceProducts.map((product) => {
                                    const mappingRowState = hasVehicleContext ? getMappingRowState(product.id) : 'unmapped';
                                    const displayTitle = truncateText(product.title, PRODUCT_TITLE_DISPLAY_LIMIT);
                                    const showTitleTooltip = product.title.length > PRODUCT_TITLE_DISPLAY_LIMIT;
                                    const linkId = `pf-product-${product.id}`;
                                    const checkboxId = `pf-product-check-${product.id}`;
                                    return (
                                        <s-table-row key={product.id} clickDelegate={hasVehicleContext ? checkboxId : linkId}>
                                            <s-table-cell>
                                                <s-stack direction="inline" gap="base" alignItems="center">
                                                    {hasVehicleContext ? (
                                                        <RowCheckbox
                                                            id={checkboxId}
                                                            accessibilityLabel={`${product.title} fits ${selectedVehicleLabel}`}
                                                            checked={mappingReady && draftMappedProductIdSet.has(product.id)}
                                                            onChange={(checked) => toggleProductMapping(product.id, checked)}
                                                            disabled={!mappingReady || mappingSubmitting}
                                                        />
                                                    ) : (
                                                        <RowCheckbox
                                                            id={checkboxId}
                                                            accessibilityLabel={`Select ${product.title}`}
                                                            checked={allResourcesSelected || productSelection.isSelected(product.id)}
                                                            onChange={(checked) => productSelection.toggle(product.id, checked)}
                                                        />
                                                    )}
                                                    <s-stack direction="inline" gap="small" alignItems="center">
                                                        <ProductThumbnail source={product.featuredImage} alt={product.title} size="small" />
                                                        <s-stack gap="none">
                                                            <s-link
                                                                id={linkId}
                                                                href={buildProductAdminUrl(product.id)}
                                                                accessibilityLabel={product.title}
                                                                interestFor={showTitleTooltip ? `${linkId}-tip` : undefined}
                                                                onClick={(event) => handleTitleLinkClick(event, `/products/${product.id}`)}
                                                            >
                                                                {displayTitle}
                                                            </s-link>
                                                            {showTitleTooltip && titleTooltip(`${linkId}-tip`, product.title)}
                                                        </s-stack>
                                                    </s-stack>
                                                </s-stack>
                                            </s-table-cell>
                                            <s-table-cell>
                                                {productStatusLabel(product.status)}
                                            </s-table-cell>
                                            <s-table-cell>{product.sku || '-'}</s-table-cell>
                                            {hasActiveAtdProfile && <s-table-cell>{product.isAtdProduct ? 'ATD' : 'Shopify'}</s-table-cell>}
                                            <s-table-cell>
                                                {product.fitmentCount.toLocaleString()}
                                            </s-table-cell>
                                            {hasVehicleContext && (
                                                <s-table-cell>
                                                    {mappingRowState === 'existing' && 'Fits'}
                                                    {mappingRowState === 'pending-add' && <s-badge tone="info">Adding</s-badge>}
                                                    {mappingRowState === 'pending-remove' && <s-badge tone="critical">Removing</s-badge>}
                                                </s-table-cell>
                                            )}
                                        </s-table-row>
                                    );
                                })}
                            </s-table-body>
                        </s-table>
                        {!loading && !productLoadError && sortedResourceProducts.length === 0 && (
                            <s-box padding="base">
                                <s-empty-state heading={hasProductFilters ? 'No products match your filters' : 'No products yet'}>
                                    <s-text slot="subheading">
                                        {hasProductFilters
                                            ? 'Try a different search or remove some filters.'
                                            : 'Products from your Shopify store will appear here.'}
                                    </s-text>
                                    {hasProductFilters && (
                                        <s-button slot="primary-action" onClick={clearProductFilters}>Clear filters</s-button>
                                    )}
                                </s-empty-state>
                            </s-box>
                        )}
                    </s-stack>
                </s-section>
            </>
                )}
                </s-stack>
            </s-page>
        </>
    );
};

export default observer(ProductsRealtimePage);
